import { useState, useSyncExternalStore } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useNetUser } from '@/hooks/useNetUser';
import { useTrackrsAttention } from '@/hooks/useTrackrsAttention';
import {
  hasSeenAttentionIntro,
  markAttentionIntroSeen,
  safeLocalStorage,
  TRACKRS_ATTENTION_PATH,
} from '@/hooks/trackrsAttention.utils';
import { AttentionMark } from '@/components/integration/attention/AttentionMark';

export const ATTENTION_INTRO_COPY =
  'Jeres sager fra Trackrs og Mesterplan er samlet. Nogle mangler oplysninger, og nogle er vi i tvivl om. ' +
  'Intet er låst — I kan arbejde videre imens.';

// "Seen" lives in localStorage, which no component re-renders on. Anything that has to wait for
// the intro (the Nyt i Mesterplan prompt) subscribes here to hear that it was closed.
const introClosedListeners = new Set<() => void>();
let introClosedVersion = 0;

function subscribeIntroClosed(listener: () => void): () => void {
  introClosedListeners.add(listener);
  return () => introClosedListeners.delete(listener);
}

function notifyIntroClosed(): void {
  introClosedVersion += 1;
  introClosedListeners.forEach((listener) => listener());
}

/** The modal's open rule (minus its own local `closed` flag), shared with the pending hook below. */
function useIntroWouldOpen(): boolean {
  const { available, openCount } = useTrackrsAttention();
  const { user } = useNetUser();
  const location = useLocation();
  // Re-evaluate when the intro is closed (the "seen" flag is read from storage below).
  useSyncExternalStore(subscribeIntroClosed, () => introClosedVersion);

  const userId = user?.id ?? null;
  const onThePage = location.pathname.startsWith(TRACKRS_ATTENTION_PATH);
  return (
    available &&
    openCount > 0 &&
    userId !== null &&
    !onThePage &&
    !hasSeenAttentionIntro(safeLocalStorage(), userId)
  );
}

/**
 * True while the intro is open or about to open, so other launch prompts yield to it (never two
 * dialogs at once). Also true while the attention request is still loading, so a slow request does
 * not let another prompt in first.
 */
export function useTrackrsAttentionIntroPending(): boolean {
  const { isLoading } = useTrackrsAttention();
  const wouldOpen = useIntroWouldOpen();
  return wouldOpen || isLoading === true;
}

/**
 * Shown once per user, the first time the list has something in it after the
 * Trackrs connection was activated. "Seen" is per-user local storage (a shared
 * browser must not hide it from the next admin), and it is never shown on the
 * page itself — landing there from Trackrs's deep link counts as seen.
 *
 * A nudge, not a gate: both buttons close it for good, and the list stays
 * reachable from the Projekter badge and the Trackrs card.
 */
export function TrackrsAttentionIntroModal() {
  const { user } = useNetUser();
  const navigate = useNavigate();
  const wouldOpen = useIntroWouldOpen();
  const { openCount } = useTrackrsAttention();
  const [closed, setClosed] = useState(false);

  const userId = user?.id ?? null;
  const open = !closed && wouldOpen;

  const close = () => {
    markAttentionIntroSeen(safeLocalStorage(), userId);
    setClosed(true);
    notifyIntroClosed();
  };

  if (!open) return null;

  return (
    <Dialog open onOpenChange={(next) => !next && close()}>
      <DialogContent className="max-w-md" role="dialog" aria-labelledby="trackrs-attention-intro-title">
        <DialogHeader onClose={close}>
          <DialogTitle id="trackrs-attention-intro-title" className="flex items-center gap-3">
            <AttentionMark />
            Vi har brug for din hjælp her
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <p className="text-[14px] text-muted dark:text-dark-muted">{ATTENTION_INTRO_COPY}</p>
          <p className="text-[14px] font-medium text-dark dark:text-dark-text">
            {openCount === 1 ? '1 ting venter på jer.' : `${openCount} ting venter på jer.`}
          </p>
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={close}>
            Senere
          </Button>
          <Button
            type="button"
            onClick={() => {
              close();
              navigate(TRACKRS_ATTENTION_PATH);
            }}
          >
            Vis mig listen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
